import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  DaySchedule,
  HolidayEntry,
  SpecialSchedule,
  WeekDayKey,
  WeekSchedule,
} from '@shared-models';
import { createEmptyWeek, weekDays } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/** Días en inglés (API) ↔ español (UI). */
type ApiWeekDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

const UI_TO_API_DAY: Record<WeekDayKey, ApiWeekDay> = {
  lunes: 'monday',
  martes: 'tuesday',
  miercoles: 'wednesday',
  jueves: 'thursday',
  viernes: 'friday',
  sabado: 'saturday',
  domingo: 'sunday',
};

const API_TO_UI_DAY: Record<ApiWeekDay, WeekDayKey> = {
  monday: 'lunes',
  tuesday: 'martes',
  wednesday: 'miercoles',
  thursday: 'jueves',
  friday: 'viernes',
  saturday: 'sabado',
  sunday: 'domingo',
};

export interface CompanyScheduleTimeRange {
  id?: string;
  startTime: string;
  endTime: string;
}

export interface CompanyScheduleDay {
  enabled: boolean;
  ranges: CompanyScheduleTimeRange[];
}

export type CompanyScheduleWeek = Record<ApiWeekDay, CompanyScheduleDay>;

export interface CompanyScheduleSpecial {
  id?: string;
  date: string;
  startTime: string;
  endTime: string;
  applyEveryYear: boolean;
}

export interface CompanyScheduleBlock {
  week: CompanyScheduleWeek;
  specials: CompanyScheduleSpecial[];
}

export interface CompanyScheduleHoliday {
  id?: string;
  month: number;
  day: number;
  name: string;
  worksOnHoliday: boolean;
  workStartTime?: string | null;
  workEndTime?: string | null;
}

/** Respuesta GET `/structure/companies/{id}/schedules` */
export interface CompanySchedule {
  companyId?: string;
  attention?: CompanyScheduleBlock;
  payments?: CompanyScheduleBlock;
  holidays?: CompanyScheduleHoliday[];
}

/** Body PUT `/structure/companies/{id}/schedules` (sin ids). */
export interface CompanyScheduleUpsertPayload {
  attention: CompanyScheduleBlock;
  payments: CompanyScheduleBlock;
  holidays: CompanyScheduleHoliday[];
}

/** Forma UI que consume el form / schedule-block. */
export interface CompanyScheduleUiState {
  scheduleAtencion: WeekSchedule;
  schedulePagos: WeekSchedule;
  holidays: HolidayEntry[];
  specialAtencion: SpecialSchedule[];
  specialPagos: SpecialSchedule[];
}

/**
 * Sub-recurso de horarios de empresa.
 *
 * Endpoints:
 *   - GET `/api/structure/companies/{companyId}/schedules`
 *   - PUT `/api/structure/companies/{companyId}/schedules` (JSON)
 */
@Injectable({ providedIn: 'root' })
export class CompanyScheduleService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanySchedule | null> {
    return this.httpClient
      .get<ApiResponse<CompanySchedule | null>>(
        `${this.apiUrl}/${companyId}/schedules`,
      )
      .pipe(map((response) => response.data ?? null));
  }

  update(
    companyId: string,
    payload: CompanyScheduleUpsertPayload,
  ): Observable<CompanySchedule | null> {
    return this.httpClient
      .put<ApiResponse<CompanySchedule | null>>(
        `${this.apiUrl}/${companyId}/schedules`,
        payload,
      )
      .pipe(map((response) => response.data ?? null));
  }

  /** API → estado UI del schedule-block. */
  toUiState(schedule: CompanySchedule | null): CompanyScheduleUiState {
    return {
      scheduleAtencion: this.mapApiWeekToUi(schedule?.attention?.week),
      schedulePagos: this.mapApiWeekToUi(schedule?.payments?.week),
      specialAtencion: this.mapApiSpecialsToUi(schedule?.attention?.specials),
      specialPagos: this.mapApiSpecialsToUi(schedule?.payments?.specials),
      holidays: this.mapApiHolidaysToUi(schedule?.holidays),
    };
  }

  /** Estado UI → body PUT (sin ids). */
  toUpsertPayload(ui: CompanyScheduleUiState): CompanyScheduleUpsertPayload {
    return {
      attention: {
        week: this.mapUiWeekToApi(ui.scheduleAtencion),
        specials: this.mapUiSpecialsToApi(ui.specialAtencion),
      },
      payments: {
        week: this.mapUiWeekToApi(ui.schedulePagos),
        specials: this.mapUiSpecialsToApi(ui.specialPagos),
      },
      holidays: this.mapUiHolidaysToApi(ui.holidays),
    };
  }

  private mapApiWeekToUi(
    week: Partial<CompanyScheduleWeek> | null | undefined,
  ): WeekSchedule {
    const empty = createEmptyWeek();
    if (!week) return empty;

    const result = createEmptyWeek();
    (Object.keys(API_TO_UI_DAY) as ApiWeekDay[]).forEach((apiDay) => {
      const uiDay = API_TO_UI_DAY[apiDay];
      result[uiDay] = this.mapApiDayToUi(week[apiDay]);
    });
    return result;
  }

  private mapApiDayToUi(
    day: CompanyScheduleDay | null | undefined,
  ): DaySchedule {
    const ranges = day?.ranges ?? [];
    const first = ranges[0];
    return {
      enabled: !!day?.enabled,
      startTime: first?.startTime ?? '',
      endTime: first?.endTime ?? '',
      extraRanges: ranges.slice(1).map((r, index) => ({
        id: r.id ?? `extra-${index + 1}`,
        startTime: r.startTime ?? '',
        endTime: r.endTime ?? '',
      })),
    };
  }

  private mapUiWeekToApi(week: WeekSchedule): CompanyScheduleWeek {
    const result = {} as CompanyScheduleWeek;
    weekDays.forEach(({ key }) => {
      const apiDay = UI_TO_API_DAY[key];
      result[apiDay] = this.mapUiDayToApi(week[key]);
    });
    return result;
  }

  private mapUiDayToApi(day: DaySchedule): CompanyScheduleDay {
    if (!day?.enabled) {
      return { enabled: false, ranges: [] };
    }

    const ranges: CompanyScheduleTimeRange[] = [];
    if (day.startTime && day.endTime) {
      ranges.push({ startTime: day.startTime, endTime: day.endTime });
    }
    for (const extra of day.extraRanges ?? []) {
      if (extra.startTime && extra.endTime) {
        ranges.push({
          startTime: extra.startTime,
          endTime: extra.endTime,
        });
      }
    }

    return { enabled: true, ranges };
  }

  private mapApiSpecialsToUi(
    specials: CompanyScheduleSpecial[] | null | undefined,
  ): SpecialSchedule[] {
    return (specials ?? []).map((s, index) => ({
      id: s.id ?? `special-${index + 1}`,
      date: s.date ?? '',
      startTime: s.startTime ?? '',
      endTime: s.endTime ?? '',
      applyEveryYear: !!s.applyEveryYear,
    }));
  }

  private mapUiSpecialsToApi(
    specials: SpecialSchedule[],
  ): CompanyScheduleSpecial[] {
    return (specials ?? [])
      .filter((s) => !!s.date)
      .map((s) => ({
        date: s.date,
        startTime: s.startTime || '',
        endTime: s.endTime || '',
        applyEveryYear: !!s.applyEveryYear,
      }));
  }

  private mapApiHolidaysToUi(
    holidays: CompanyScheduleHoliday[] | null | undefined,
  ): HolidayEntry[] {
    return (holidays ?? []).map((h, index) => ({
      id: h.id ?? `holiday-${index + 1}`,
      month: h.month != null ? String(h.month) : '',
      day:
        h.day != null
          ? String(h.day).padStart(2, '0')
          : '',
      name: h.name ?? '',
      worksOnHoliday: !!h.worksOnHoliday,
      workStartTime: h.workStartTime ?? '',
      workEndTime: h.workEndTime ?? '',
    }));
  }

  private mapUiHolidaysToApi(
    holidays: HolidayEntry[],
  ): CompanyScheduleHoliday[] {
    return (holidays ?? [])
      .map((h) => {
        const month = parseInt(String(h.month ?? ''), 10);
        const day = parseInt(String(h.day ?? ''), 10);
        return { h, month, day };
      })
      .filter(
        ({ h, month, day }) =>
          Number.isFinite(month) &&
          month >= 1 &&
          month <= 12 &&
          Number.isFinite(day) &&
          day >= 1 &&
          day <= 31 &&
          !!h.name?.trim(),
      )
      .map(({ h, month, day }) => {
        const item: CompanyScheduleHoliday = {
          month,
          day,
          name: h.name.trim(),
          worksOnHoliday: !!h.worksOnHoliday,
        };
        if (h.worksOnHoliday) {
          item.workStartTime = h.workStartTime || null;
          item.workEndTime = h.workEndTime || null;
        }
        return item;
      });
  }
}
